/**
 * The text of a condition on a chip (design §5.5, addendum A.5.3): "Categorie is Ecosysteempartner
 * of Co-investeerder", "Gewijzigd: afgelopen 2 weken · 3–16 okt". A relative date always shows the
 * days it covers, so the chip never leaves the reader to work out what "afgelopen 2 weken" is today.
 * The labels of the operators come from the register; ids that cannot be named read "Onbekend".
 */
import { addDays, formatDay, formatRange, formatShortDate, isDayCount, pastRange, periodRange } from './dateRanges'
import type { Condition, OperatorDef, PropertyDef } from './types'

export interface DescribeContext {
  /** Names of the ids in list values (labels, categories, team members, organisations, persons). */
  names: ReadonlyMap<string, string>
  /** Today as YYYY-MM-DD in Amsterdam. */
  today: string
}

/** The label a property goes by on a chip, where it differs from the register's. */
const CHIP_LABELS: Record<string, string> = { last_activity_at: 'Gewijzigd', last_activity_by: 'Gewijzigd door' }
/** What "no …" reads as for the "not in the last n days" operators. */
const NOTHING: Record<string, string> = { last_contact_at: 'Geen contact', last_activity_at: 'Geen activiteit' }
/** Moments in the past only: "deze week" is then shown up to today. */
const PAST_ONLY = new Set(['last_activity_at', 'last_contact_at', 'created_at', 'updated_at'])
const DAY_COUNT_OPERATORS = new Set(['in_last_days', 'not_in_last_days', 'more_than_days_ago', 'in_next_days', 'more_than_days_ahead'])
const PERIODS: Record<string, string> = {
  this_week: 'deze week',
  this_month: 'deze maand',
  this_quarter: 'dit kwartaal',
  this_year: 'dit jaar',
}

function values(condition: Condition): string[] {
  return Array.isArray(condition.value) ? condition.value.map(String) : []
}

function nameOf(value: string, property: PropertyDef, names: ReadonlyMap<string, string>): string {
  if (property.type === 'user') {
    if (value === 'me') return 'Ik'
    if (value === 'none') return 'Niemand'
  }
  const fixed = property.options?.source === 'static' ? property.options.values.find((option) => option.value === value) : undefined
  return fixed?.label ?? names.get(value) ?? 'Onbekend'
}

function describeList(condition: Condition, operator: OperatorDef, property: PropertyDef, label: string, names: ReadonlyMap<string, string>): string {
  const shown = values(condition).map((value) => nameOf(value, property, names))
  if (shown.length === 0) return `${label} ${operator.label}` // no value to name: a link with a broken one
  if (property.type === 'user') return `${label}: ${operator.key === 'is_none_of' ? 'niet ' : ''}${shown.join(', ')}`
  const one = shown.length === 1
  switch (operator.key) {
    case 'is_any_of':
      return `${label} is ${shown.join(' of ')}`
    case 'is_none_of':
      return one ? `${label} is niet ${shown[0]}` : `${label} is geen van ${shown.join(', ')}`
    case 'contains_any_of':
      return one ? `${label} bevat ${shown[0]}` : `${label} bevat een van ${shown.join(', ')}`
    case 'contains_all_of':
      return `${label} bevat ${shown.join(' en ')}`
    case 'contains_none_of':
      return one ? `${label} bevat geen ${shown[0]}` : `${label} bevat geen van ${shown.join(', ')}`
    default:
      return `${label} ${operator.label} ${shown.join(', ')}`
  }
}

/** "afgelopen 2 weken" for 14 days; one day is "vandaag". */
function lastDays(days: number): string {
  if (days === 1) return 'vandaag'
  return days === 14 ? 'afgelopen 2 weken' : `afgelopen ${days} dagen`
}

/** The operators that take a number of days; they read as the plain operator when the number is no good. */
function describeDays(condition: Condition, days: number, property: PropertyDef, label: string, today: string): string | null {
  const past = pastRange(condition, today)
  switch (condition.operator) {
    case 'in_last_days':
      return past && 'from' in past ? `${label}: ${lastDays(days)} · ${formatRange(past, today)}` : null
    case 'not_in_last_days': {
      if (!past || !('before' in past)) return null
      const nothing = NOTHING[property.key] ?? `${label}: niet`
      return `${nothing} ${days === 1 ? 'vandaag' : `in de afgelopen ${days} dagen`} · vóór ${formatShortDate(past.before, today)}`
    }
    case 'more_than_days_ago':
      return past && 'before' in past
        ? `${label}: meer dan ${days} dagen geleden · vóór ${formatShortDate(past.before, today)}`
        : null
    case 'in_next_days': {
      const window = days === 1 ? 'vandaag en morgen' : `komende ${days} dagen`
      return `${label}: ${window} · ${formatRange({ from: today, to: addDays(today, days) }, today)}`
    }
    case 'more_than_days_ahead':
      return `${label}: meer dan ${days} dagen vooruit · na ${formatShortDate(addDays(today, days), today)}`
    default:
      return null
  }
}

/** The text of a date condition with the days it covers, or null for an operator that has none. */
function describeDate(condition: Condition, property: PropertyDef, label: string, today: string): string | null {
  const { operator, value } = condition
  if (DAY_COUNT_OPERATORS.has(operator)) {
    return isDayCount(value) ? describeDays(condition, value, property, label, today) : null
  }
  const past = pastRange(condition, today)
  switch (operator) {
    case 'on':
      return `${label}: ${formatDay(String(value), today)}`
    case 'before':
      return `${label} vóór ${formatShortDate(String(value), today)}`
    case 'after':
      return `${label} na ${formatShortDate(String(value), today)}`
    case 'between':
      return past && 'from' in past ? `${label}: ${formatRange(past, today)}` : null
    case 'not_since':
      return `${NOTHING[property.key] ?? `${label}: niet`} sinds ${formatShortDate(String(value), today)}`
    case 'this_week':
    case 'this_month':
    case 'this_quarter':
    case 'this_year': {
      const range = operator === 'this_week' && PAST_ONLY.has(property.key) ? past : periodRange(operator, today)
      return range && 'from' in range ? `${label}: ${PERIODS[operator]} · ${formatRange(range, today)}` : null
    }
    default:
      return null
  }
}

export function describeCondition(condition: Condition, property: PropertyDef | undefined, context: DescribeContext): string {
  const operator = property?.operators.find((candidate) => candidate.key === condition.operator)
  if (!property || !operator) return 'Onbekend filter'
  const label = CHIP_LABELS[property.key] ?? property.label
  if (property.type === 'date' || property.type === 'datetime') {
    const text = describeDate(condition, property, label, context.today)
    if (text !== null) return text
  }
  switch (operator.value) {
    case 'text':
      return `${label} ${operator.label} “${String(condition.value ?? '')}”`
    case 'list':
      return describeList(condition, operator, property, label, context.names)
    case 'number':
      return `${label} ${operator.label} ${String(condition.value ?? '')}`
    case 'number_range': {
      const [low, high] = Array.isArray(condition.value) ? condition.value : []
      return `${label} ${operator.label} ${low} en ${high}`
    }
    default:
      return `${label} ${operator.label}`
  }
}
